import { ReactNode, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

interface MarkdownTableProps {
  children?: ReactNode;
}

/**
 * Markdown table rendered as a bordered card with a sticky header row, plus an
 * Excel export of what the table currently shows.
 *
 * Lifted out of Answer.tsx's `components.table` override, where the useRef sat
 * inside a lowercase inline render function and tripped rules-of-hooks. Cell
 * styling lives in index.css under `table.md-table`, because the
 * `.markdown-body table th/td` reset there outranks any utility class.
**/
const MarkdownTable = ({ children }: MarkdownTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null);

  /**
   * Handle exporting the rendered table to an Excel workbook
  **/
  const handleExport = async () => {
    try {
      if (!tableRef.current) return;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1");

      // Extract table data
      const rows = tableRef.current.querySelectorAll("tr");
      rows.forEach((row) => {
        const cells = row.querySelectorAll("th, td");
        const rowData = Array.from(cells).map((cell) => cell.textContent || "");
        worksheet.addRow(rowData);
      });

      // Auto-fit columns
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell?.({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? cell.value.toString() : "";
          maxLength = Math.max(maxLength, cellValue.length);
        });
        column.width = Math.min(maxLength + 2, 50);
      });

      // Write to file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, "data.xlsx");
    } catch (err) {
      console.error("Excel export failed:", err);
    }
  };

  return (
    <div className="my-3">
      <div className="overflow-hidden rounded-[10px] ring-1 ring-line">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="md-table !mb-0">{children}</table>
        </div>
      </div>
      <button
        type="button"
        className="mt-1.5 inline-flex items-center gap-1.5 rounded-control px-1.5 py-1 text-[12px] text-ink-2 transition-colors hover:bg-hover hover:text-ink"
        onClick={handleExport}
      >
        <FontAwesomeIcon icon={faFileExcel} /> Export to Excel
      </button>
    </div>
  );
};

export default MarkdownTable;
